<template>
  <div class="rights-concepts">
    <v-container>
      <v-autocomplete
        label="Users"
        chips
        filled
        solo
        item-value="uuid"
        item-text="first_name"
        return-object
        v-model="currentUser"
        :items="organizationUsers"
        :filter="customFilter"
      >
        <template v-slot:selection="data">
          <v-chip
            v-bind="data.attrs"
            :input-value="data.selected"
            close
            @click="data.select"
            @click:close="remove"
          >
            <v-avatar left>
              <v-icon>mdi-account</v-icon>
            </v-avatar>
            {{ data.item.first_name }} {{ data.item.last_name }}
          </v-chip>
        </template>
        <template v-slot:item="data">
          <v-list-item-avatar>
            <v-icon>mdi-account</v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title
              v-html="data.item.first_name + ' ' + data.item.last_name"
            ></v-list-item-title>
            <v-list-item-subtitle
              v-html="data.item.email"
            ></v-list-item-subtitle>
          </v-list-item-content>
        </template>
        <template v-slot:append-outer>
          <v-btn color="green" @click="getUser" dark>Search</v-btn>
        </template>
      </v-autocomplete>
      <div v-if="userRights.uuid">
        <div class="">
          <v-card tile>
            <v-card-title>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title class="title"
                    >{{ userRights.first_name }}
                    {{ userRights.last_name }}</v-list-item-title
                  >
                  <v-list-item-subtitle
                    >{{ userRights.email }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle
                    >{{ userRights.uuid }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-card-title>
            <v-card-text>
              <v-expansion-panels>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    Locations
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-list>
                      <template v-for="loc in sortedLocations">
                        <v-divider :key="loc.uuid + '-div'"></v-divider>
                        <v-list-item :key="loc.uuid">
                          <v-list-item-avatar>
                            {{
                              getIconByCountry(
                                loc.location_country.country_code
                              )
                            }}
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title>{{
                              loc.label
                            }}</v-list-item-title>
                          </v-list-item-content>
                          <v-list-item-content>
                            <v-list-item-title>
                              <v-chip
                                x-small
                                :class="[
                                  'ma-1',
                                  'font-weight-bold',
                                  getKitchenFromLocation(loc).is_deleted
                                    ? ''
                                    : 'white--text',
                                ]"
                                :color="
                                  getKitchenFromLocation(loc).is_deleted
                                    ? 'orange'
                                    : 'green'
                                "
                              >
                                {{
                                  getKitchenFromLocation(loc).is_deleted
                                    ? "Deleted"
                                    : "Active"
                                }}
                              </v-chip>
                            </v-list-item-title>
                          </v-list-item-content>
                          <v-list-item-content>
                            <v-icon x-small></v-icon>
                            {{
                              getKitchenFromLocation(loc).is_franchise
                                ? "🤝"
                                : "👅"
                            }}
                          </v-list-item-content>
                          <v-list-item-action>
                            <div class="d-flex align-center justify-center">
                              <v-btn
                                small
                                dark
                                @click="
                                  getLocationLevel('admins').includes(loc.uuid)
                                    ? removeUserFrom(
                                        'location',
                                        'admins',
                                        loc.uuid
                                      )
                                    : addUserTo('location', 'admins', loc.uuid)
                                "
                                :color="
                                  getLocationLevel('admins').includes(loc.uuid)
                                    ? 'green'
                                    : 'red'
                                "
                              >
                                <v-icon left>{{
                                  getLocationLevel("admins").includes(loc.uuid)
                                    ? "mdi-check"
                                    : "mdi-close"
                                }}</v-icon>
                                <span>Admin</span>
                              </v-btn>
                              <v-btn
                                small
                                dark
                                class="ml-2"
                                @click="
                                  getLocationLevel('editors').includes(loc.uuid)
                                    ? removeUserFrom(
                                        'location',
                                        'editors',
                                        loc.uuid
                                      )
                                    : addUserTo('location', 'editors', loc.uuid)
                                "
                                :color="
                                  getLocationLevel('editors').includes(loc.uuid)
                                    ? 'green'
                                    : 'red'
                                "
                              >
                                <v-icon left>{{
                                  getLocationLevel("editors").includes(loc.uuid)
                                    ? "mdi-check"
                                    : "mdi-close"
                                }}</v-icon>
                                <span>Editor</span>
                              </v-btn>
                              <v-btn
                                small
                                dark
                                class="ml-2"
                                @click="
                                  getLocationLevel('viewers').includes(loc.uuid)
                                    ? removeUserFrom(
                                        'location',
                                        'viewers',
                                        loc.uuid
                                      )
                                    : addUserTo('location', 'viewers', loc.uuid)
                                "
                                :color="
                                  getLocationLevel('viewers').includes(loc.uuid)
                                    ? 'green'
                                    : 'red'
                                "
                              >
                                <v-icon left>{{
                                  getLocationLevel("viewers").includes(loc.uuid)
                                    ? "mdi-check"
                                    : "mdi-close"
                                }}</v-icon>
                                <span>Viewer</span>
                              </v-btn>
                            </div>
                          </v-list-item-action>
                        </v-list-item>
                      </template>
                      <v-divider></v-divider>
                    </v-list>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    Concepts
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-list>
                      <template v-for="concept in sortedConcepts">
                        <v-divider :key="concept.uuid + '-div'"></v-divider>
                        <v-list-item :key="concept.uuid">
                          <v-list-item-avatar>
                            <v-img
                              contain
                              height="30px"
                              :src="concept.logo"
                            ></v-img>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title>{{
                              concept.label
                            }}</v-list-item-title>
                          </v-list-item-content>
                          <v-list-item-action>
                            <div class="d-flex align-center justify-center">
                              <v-btn
                                small
                                dark
                                @click="
                                  getConceptLevel('admins').includes(
                                    concept.uuid
                                  )
                                    ? removeUserFrom(
                                        'concept',
                                        'admins',
                                        concept.uuid
                                      )
                                    : addUserTo(
                                        'concept',
                                        'admins',
                                        concept.uuid
                                      )
                                "
                                :color="
                                  getConceptLevel('admins').includes(
                                    concept.uuid
                                  )
                                    ? 'green'
                                    : 'red'
                                "
                              >
                                <v-icon left>{{
                                  getConceptLevel("admins").includes(
                                    concept.uuid
                                  )
                                    ? "mdi-check"
                                    : "mdi-close"
                                }}</v-icon>
                                <span>Admin</span>
                              </v-btn>
                              <v-btn
                                small
                                dark
                                class="ml-2"
                                @click="
                                  getConceptLevel('editors').includes(
                                    concept.uuid
                                  )
                                    ? removeUserFrom(
                                        'concept',
                                        'editors',
                                        concept.uuid
                                      )
                                    : addUserTo(
                                        'concept',
                                        'editors',
                                        concept.uuid
                                      )
                                "
                                :color="
                                  getConceptLevel('editors').includes(
                                    concept.uuid
                                  )
                                    ? 'green'
                                    : 'red'
                                "
                              >
                                <v-icon left>{{
                                  getConceptLevel("editors").includes(
                                    concept.uuid
                                  )
                                    ? "mdi-check"
                                    : "mdi-close"
                                }}</v-icon>
                                <span>Editor</span>
                              </v-btn>
                              <v-btn
                                small
                                dark
                                class="ml-2"
                                @click="
                                  getConceptLevel('viewers').includes(
                                    concept.uuid
                                  )
                                    ? removeUserFrom(
                                        'concept',
                                        'viewers',
                                        concept.uuid
                                      )
                                    : addUserTo(
                                        'concept',
                                        'viewers',
                                        concept.uuid
                                      )
                                "
                                :color="
                                  getConceptLevel('viewers').includes(
                                    concept.uuid
                                  )
                                    ? 'green'
                                    : 'red'
                                "
                              >
                                <v-icon left>{{
                                  getConceptLevel("viewers").includes(
                                    concept.uuid
                                  )
                                    ? "mdi-check"
                                    : "mdi-close"
                                }}</v-icon>
                                <span>Viewer</span>
                              </v-btn>
                            </div>
                          </v-list-item-action>
                        </v-list-item>
                      </template>
                      <v-divider></v-divider>
                    </v-list>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
          </v-card>
        </div>
      </div>
    </v-container>
    <v-snackbar v-model="snackbar" top>
      {{ errorMessage }}
      <v-btn color="red" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { orderBy } from "lodash";
import mixin from "@/mixins";
export default {
  mixins: [mixin],
  data() {
    return {
      nestedLocations: [],
      locations: [],
      errorMessage: "",
      snackbar: false,
      organizationLocations: [],
      organizationUuid: "",
      currentUser: {},
      userRights: {},
      kitchens: [],
    };
  },
  computed: {
    ...mapState({
      currentOrganization: (state) => state.organizations.current,
      organizationUsers: (state) => state.organizations.users,
      organizationUsersLoading: (state) =>
        state.organizations.fetchAllUsers.pending,
      locationsError: (state) => state.locations.error,
      organizationLocationsLoading: (state) =>
        state.organizations.fetchAllLocations.pending,
      locationsLoading: (state) => state.locations.fetchAll.pending,
      conceptsError: (state) => state.concepts.error,
      organizationConceptsLoading: (state) =>
        state.organizations.fetchAllConcepts.pending,
      conceptsLoading: (state) => state.concepts.fetchAll.pending,
    }),
    sortedLocations() {
      return orderBy(this.locations, [
        "location_country.country_code",
        "label",
      ]);
    },
    sortedConcepts() {
      return orderBy(this.concepts, ["label"]);
    },
  },
  watch: {
    currentOrganization() {
      this.refresh();
    },
  },
  methods: {
    addUserTo(type, level, uuid) {
      switch (type) {
        case "location": {
          this.addOrganizationLocationUser({
            organizationUuid: this.currentOrganization.uuid,
            locationUuid: uuid,
            userUuid: this.currentUser.uuid,
            role: level,
          })
            .then(() => {
              this.getUser();
            })
            .catch((error) => {
              this.snackbar = true;
              this.errorMessage = this.errorDisplay(error);
            });
          break;
        }
        case "concept": {
          this.addOrganizationConceptUser({
            organizationUuid: this.currentOrganization.uuid,
            conceptUuid: uuid,
            userUuid: this.currentUser.uuid,
            role: level,
          })
            .then(() => {
              this.getUser();
            })
            .catch((error) => {
              this.snackbar = true;
              this.errorMessage = this.errorDisplay(error);
            });
          break;
        }
      }
    },
    removeUserFrom(type, level, uuid) {
      switch (type) {
        case "location": {
          this.removeOrganizationLocationUser({
            organizationUuid: this.currentOrganization.uuid,
            locationUuid: uuid,
            userUuid: this.currentUser.uuid,
            role: level,
          })
            .then(() => {
              this.getUser();
            })
            .catch((error) => {
              this.snackbar = true;
              this.errorMessage = this.errorDisplay(error);
            });
          break;
        }
        case "concept": {
          this.removeOrganizationConceptUser({
            organizationUuid: this.currentOrganization.uuid,
            conceptUuid: uuid,
            userUuid: this.currentUser.uuid,
            role: level,
          })
            .then(() => {
              this.getUser();
            })
            .catch((error) => {
              this.snackbar = true;
              this.errorMessage = this.errorDisplay(error);
            });
          break;
        }
      }
    },
    getLocationLevel(lev) {
      let locs =
        (this.userRights.locations
          ? this.userRights.locations.find(({ level }) => level === lev)
          : {}) || {};
      return locs.uuids ? locs.uuids : [];
    },
    getConceptLevel(lev) {
      let cons =
        (this.userRights.concepts
          ? this.userRights.concepts.find(({ level }) => level === lev)
          : {}) || {};
      return cons.uuids ? cons.uuids : [];
    },
    ...mapActions("organizations", {
      fetchAllOrganizationLocations: "fetchAllLocations",
      fetchAllOrganizationConcepts: "fetchAllConcepts",
      fetchAllUsers: "fetchAllUsers",
      fetchOrganizationLocationUsers: "fetchOrganizationLocationUsers",
      addOrganizationLocationUser: "addOrganizationLocationUser",
      removeOrganizationLocationUser: "removeOrganizationLocationUser",
      fetchOrganizationConceptUsers: "fetchOrganizationConceptUsers",
      addOrganizationConceptUser: "addOrganizationConceptUser",
      removeOrganizationConceptUser: "removeOrganizationConceptUser",
      getOrganizationUser: "getOrganizationUser",
    }),
    ...mapActions("locations", { fetchAllLocations: "fetchAll" }),
    ...mapActions("concepts", { fetchAllConcepts: "fetchAll" }),
    ...mapActions("kitchens", { fetchAllKitchens: "fetchAll" }),
    refresh() {
      this.fetchAllLocations({ nested: true })
        .then((locations) => {
          this.nestedLocations = locations;
        })
        .catch((error) => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.fetchAllKitchens()
        .then((kitchens) => {
          this.kitchens = kitchens;
        })
        .catch((error) => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.fetchAllLocations({ nested: false })
        .then((locations) => {
          this.locations = locations;
        })
        .catch((error) => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.fetchAllOrganizationLocations({
        organizationUuid: this.currentOrganization.uuid,
      })
        .then((locations) => {
          this.organizationLocations = locations;
        })
        .catch((error) => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.fetchAllUsers({
        organizationUuid: this.currentOrganization.uuid,
      }).catch((error) => {
        this.snackbar = true;
        this.errorMessage = this.errorDisplay(error);
      });
      this.fetchAllConcepts()
        .then((concepts) => {
          this.concepts = concepts;
        })
        .catch((error) => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.fetchAllOrganizationConcepts({
        organizationUuid: this.currentOrganization.uuid,
      })
        .then((concepts) => {
          this.organizationConcepts = concepts;
        })
        .catch((error) => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.fetchAllUsers({
        organizationUuid: this.currentOrganization.uuid,
      }).catch((error) => {
        this.snackbar = true;
        this.errorMessage = this.errorDisplay(error);
      });
    },
    remove() {
      this.currentUser = {};
    },
    customFilter(item, queryText) {
      const textOne = item.first_name.toLowerCase();
      const textTwo = item.last_name.toLowerCase();
      const textThree = item.email.toLowerCase();
      const searchText = queryText.toLowerCase();

      return (
        textOne.indexOf(searchText) > -1 ||
        textTwo.indexOf(searchText) > -1 ||
        textThree.indexOf(searchText) > -1
      );
    },
    async getUser() {
      let response = await this.getOrganizationUser({
        organizationUuid: this.currentOrganization.uuid,
        userUuid: this.currentUser.uuid,
      });
      this.userRights = response.data;
    },
    getIconByCountry(country) {
      let icons = {
        ES: "🇪🇸",
        UK: "🇬🇧",
        FR: "🇫🇷",
        UAE: "🇦🇪",
        BE: "🇧🇪",
      };
      return icons[country];
    },
    getKitchenFromLocation(location) {
      if (location.kitchens.length === 0)
        return { is_deleted: location.is_deleted };
      let found = this.kitchens.find(
        ({ uuid }) => location.kitchens[0] === uuid
      );
      return found ? found : {};
    },
  },
  created() {
    if (this.currentOrganization) {
      this.refresh();
    }
  },
};
</script>

<style></style>
